import type {
  InterviewItem,
  InterviewItemTemplate,
  MaxDiffDesign,
  OptionId,
} from "../types";
import { computeScreenCount, generateMaxDiffDesign } from "./generateDesign";

export { generateMaxDiffDesign, computeScreenCount } from "./generateDesign";
export type { MaxDiffDesign } from "../types";

export const DEFAULT_MAX_DIFF_PER_SCREEN = 4;
export const DEFAULT_MAX_DIFF_TARGET_APPEARANCES = 3;

type MaxDiffSizedItem = {
  itemsPerScreen?: InterviewItemTemplate["itemsPerScreen"];
  choices?: { id: OptionId }[] | null;
};

const getPerScreen = (item: MaxDiffSizedItem): number =>
  item.itemsPerScreen ?? DEFAULT_MAX_DIFF_PER_SCREEN;

const getItemIds = (item: MaxDiffSizedItem): OptionId[] =>
  (item.choices ?? []).map((c) => c.id);

const hashSeed = (seed: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index++) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

// The per-screen size respondents actually see: the saved size clamped to the
// number of unique items (you can't show more distinct items than exist).
export const getMaxDiffEffectivePerScreen = (
  item: MaxDiffSizedItem
): number => {
  const itemCount = new Set(getItemIds(item)).size;
  return Math.max(1, Math.min(getPerScreen(item), itemCount || 1));
};

export const getMaxDiffTotalScreens = (
  item: InterviewItem | InterviewItemTemplate
): number => {
  const itemCount = new Set(getItemIds(item)).size;
  return computeScreenCount({
    itemCount,
    perScreen: getMaxDiffEffectivePerScreen(item),
    targetAppearances: DEFAULT_MAX_DIFF_TARGET_APPEARANCES,
  });
};

// A cached design is reusable only while it still matches the item: same
// screen width, screen count, and option set. Revision edits invalidate it.
export const isMaxDiffDesignCurrent = (
  item: InterviewItem | InterviewItemTemplate,
  design: MaxDiffDesign
): boolean => {
  const expectedIds = new Set(getItemIds(item));
  const designIds = new Set(design.screens.flat());
  const perScreen = getMaxDiffEffectivePerScreen(item);
  return (
    design.screens.length === getMaxDiffTotalScreens(item) &&
    design.screens.every((screen) => screen.length === perScreen) &&
    designIds.size === expectedIds.size &&
    [...designIds].every((id) => expectedIds.has(id))
  );
};

export const buildMaxDiffDesign = (
  item: InterviewItem | InterviewItemTemplate,
  seedBase?: string
): MaxDiffDesign =>
  generateMaxDiffDesign({
    itemIds: getItemIds(item),
    perScreen: getPerScreen(item),
    targetAppearances: DEFAULT_MAX_DIFF_TARGET_APPEARANCES,
    seed: hashSeed(`${seedBase ?? "preview"}:${item.id}`),
  });
